import { createContext, useContext, useEffect, useRef, useState, useCallback, ReactNode } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuthContext } from '@/contexts/AuthContext';

export interface PresenceUserState {
  user_id: string;
  role: string;
  year_id?: string;
  module_name?: string;
  topic_name?: string;
  page: string;
  section_mode?: string;
  active_tab?: string;
}

export interface OnlineUser {
  presence_ref: string;
  state: PresenceUserState;
}

interface PresenceContextType {
  updatePresence: (pageState: Omit<PresenceUserState, 'user_id' | 'role'>) => void;
  onlineUsers: OnlineUser[];
  onlineCount: number;
  /** Number of open sessions seen in the last few minutes (a user with two tabs counts twice). */
  wsCount: number;
  /** True only for users allowed to read everyone's sessions (platform admin and above). */
  canViewOnline: boolean;
}

const PresenceContext = createContext<PresenceContextType | undefined>(undefined);

/*
 * Presence WITHOUT the shared Supabase Realtime channel.
 *
 * Before (load test, 27 Sep 2026): every signed-in user joined one presence channel
 * and every page change was pushed to every other user online. That traffic grows
 * with the square of the number of users and passes Supabase Pro limits (500
 * connections / 500 messages per second with the spend cap on) at a few hundred
 * students.
 *
 * Now: each user writes their current page into their own user_sessions row — the
 * row useSessionTracking already keeps fresh with a 60-second heartbeat. Only
 * platform admins and super admins (the roles the "Platform admins can view all
 * sessions" rule lets read every row) poll those rows, every 30 seconds.
 */
const SESSION_ID_KEY = 'current_session_id'; // written by useSessionTracking
const ONLINE_WINDOW_MS = 150_000;            // heartbeat is 60 s; allow two missed beats
const POLL_MS = 30_000;
const WRITE_DEBOUNCE_MS = 3_000;
const RETRY_MS = 60_000;
const PAGE_SIZE = 1000;                       // the API returns at most 1,000 rows per request
const VIEWER_ROLES = ['platform_admin', 'super_admin'];

// presence_state is a new column not yet in the generated Supabase types.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sessionsTable = () => (supabase as any).from('user_sessions');

function readSessionId(): string | null {
  try {
    return localStorage.getItem(SESSION_ID_KEY);
  } catch {
    return null;
  }
}

export function PresenceProvider({ children }: { children: ReactNode }) {
  const { user, profile, role } = useAuthContext();
  const baseStateRef = useRef<Pick<PresenceUserState, 'user_id' | 'role' | 'year_id'> | null>(null);
  const currentStateRef = useRef<PresenceUserState | null>(null);
  const dirtyRef = useRef(false);
  const writeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [onlineUsers, setOnlineUsers] = useState<OnlineUser[]>([]);
  const [wsCount, setWsCount] = useState(0);

  const userId = user?.id;
  const canViewOnline = !!userId && !!role && VIEWER_ROLES.includes(role);

  // Write the current page into this user's own session row.
  const flush = useCallback(async () => {
    if (!userId || !currentStateRef.current || !dirtyRef.current) return;
    const sessionId = readSessionId();
    if (!sessionId) return; // session row not created yet — the retry timer tries again
    dirtyRef.current = false;
    const { error } = await sessionsTable()
      .update({ presence_state: currentStateRef.current, last_seen_at: new Date().toISOString() })
      .eq('id', sessionId)
      .eq('user_id', userId);
    if (error) dirtyRef.current = true; // try again on the next change or retry tick
  }, [userId]);

  const scheduleFlush = useCallback(() => {
    if (writeTimerRef.current) clearTimeout(writeTimerRef.current);
    writeTimerRef.current = setTimeout(() => { void flush(); }, WRITE_DEBOUNCE_MS);
  }, [flush]);

  const updatePresence = useCallback((pageState: Omit<PresenceUserState, 'user_id' | 'role'>) => {
    if (!baseStateRef.current) return;
    currentStateRef.current = { ...baseStateRef.current, ...pageState };
    dirtyRef.current = true;
    scheduleFlush();
  }, [scheduleFlush]);

  // Start / stop tracking on login / logout
  useEffect(() => {
    if (!userId) {
      baseStateRef.current = null;
      currentStateRef.current = null;
      dirtyRef.current = false;
      setOnlineUsers([]);
      setWsCount(0);
      return;
    }

    const initialBase = {
      user_id: userId,
      role: role || 'student',
      year_id: profile?.preferred_year_id ?? undefined,
    };
    baseStateRef.current = initialBase;
    currentStateRef.current = { ...initialBase, page: 'home' };
    dirtyRef.current = true;
    scheduleFlush();

    const retry = setInterval(() => { void flush(); }, RETRY_MS);
    return () => {
      clearInterval(retry);
      if (writeTimerRef.current) clearTimeout(writeTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // Update the base state when role or preferred year loads after login
  useEffect(() => {
    if (!userId || !currentStateRef.current) return;
    const newBase = {
      user_id: userId,
      role: role || 'student',
      year_id: profile?.preferred_year_id ?? undefined,
    };
    baseStateRef.current = newBase;
    currentStateRef.current = { ...currentStateRef.current, ...newBase };
    dirtyRef.current = true;
    scheduleFlush();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [role, profile?.preferred_year_id]);

  // Admins only: read who has been active in the last few minutes, every 30 seconds
  useEffect(() => {
    if (!canViewOnline) {
      setOnlineUsers([]);
      setWsCount(0);
      return;
    }
    let cancelled = false;

    const load = async () => {
      const since = new Date(Date.now() - ONLINE_WINDOW_MS).toISOString();
      const rows: { id: string; user_id: string; presence_state: PresenceUserState | null }[] = [];
      for (let from = 0; ; from += PAGE_SIZE) {
        const { data, error } = await sessionsTable()
          .select('id, user_id, last_seen_at, presence_state')
          .gte('last_seen_at', since)
          .is('session_end', null)
          .order('last_seen_at', { ascending: false })
          .range(from, from + PAGE_SIZE - 1);
        if (error || !data) break;
        rows.push(...data);
        if (data.length < PAGE_SIZE) break;
      }
      if (cancelled) return;

      // One entry per user: rows are newest first, so keep the first seen.
      const byUser = new Map<string, OnlineUser>();
      for (const r of rows) {
        if (byUser.has(r.user_id)) continue;
        byUser.set(r.user_id, {
          presence_ref: r.id,
          state: r.presence_state ?? { user_id: r.user_id, role: 'student', page: 'other' },
        });
      }
      setOnlineUsers(Array.from(byUser.values()));
      setWsCount(rows.length);
    };

    void load();
    const poll = setInterval(() => { void load(); }, POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(poll);
    };
  }, [canViewOnline]);

  return (
    <PresenceContext.Provider value={{ updatePresence, onlineUsers, onlineCount: onlineUsers.length, wsCount, canViewOnline }}>
      {children}
    </PresenceContext.Provider>
  );
}

export function usePresence() {
  const context = useContext(PresenceContext);
  if (context === undefined) {
    throw new Error('usePresence must be used within a PresenceProvider');
  }
  return context;
}
